import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import { Label } from 'src/components/ui/label';
import { AlertContainer, AlertDescription } from 'src/components/ui/alert';
import {
  CircleNotchIcon,
  WarningCircleIcon,
  InfoIcon,
} from '@phosphor-icons/react';
import { useDuplicateWorkflowDefinition } from 'src/api/rpc/hooks/workflows/useWorkflowDefinitions';
import { useToast } from 'src/context/ToastContext';

interface DuplicateWorkflowModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (newWorkflowId: string) => void;
  sourceWorkflow: {
    id: string;
    name: string;
  } | null;
}

export function DuplicateWorkflowModal({
  open,
  onClose,
  onSuccess,
  sourceWorkflow,
}: DuplicateWorkflowModalProps) {
  const [name, setName] = useState('');
  const [error, setError] = useState<string>('');
  const { showToast } = useToast();
  const duplicateMutation = useDuplicateWorkflowDefinition();

  useEffect(() => {
    if (sourceWorkflow && open) {
      setName(`${sourceWorkflow.name} (Copy)`);
      setError('');
    }
  }, [sourceWorkflow, open]);

  const handleClose = () => {
    if (!duplicateMutation.isPending) {
      setName('');
      setError('');
      onClose();
    }
  };

  const handleSubmit = async () => {
    setError('');

    if (!name.trim()) {
      setError('Name is required.');
      return;
    }

    if (!sourceWorkflow) {
      setError('No workflow selected.');
      return;
    }

    try {
      const result = await duplicateMutation.mutateAsync({
        workflowDefinitionId: sourceWorkflow.id,
        name: name.trim(),
      });

      showToast({
        title: 'Workflow duplicated',
        message: 'The workflow has been duplicated successfully.',
        color: 'success',
      });

      onSuccess?.(result.id);
      handleClose();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to duplicate workflow.',
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Duplicate Workflow</DialogTitle>
          <DialogDescription>
            Create a copy of "{sourceWorkflow?.name}"
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="workflow-name">Name</Label>
            <Input
              id="workflow-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter workflow name"
              disabled={duplicateMutation.isPending}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !duplicateMutation.isPending) {
                  void handleSubmit();
                }
              }}
            />
          </div>

          <AlertContainer>
            <InfoIcon className="h-4 w-4" />
            <AlertDescription>
              The duplicated workflow will be created as{' '}
              <span className="font-medium">inactive</span>. You can activate it
              after reviewing its configuration.
            </AlertDescription>
          </AlertContainer>

          {error && (
            <AlertContainer variant="destructive">
              <WarningCircleIcon className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </AlertContainer>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={duplicateMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={duplicateMutation.isPending || !name.trim()}
          >
            {duplicateMutation.isPending && (
              <CircleNotchIcon className="mr-2 h-4 w-4 animate-spin" />
            )}
            Duplicate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
